import { useMutation, useQueryClient } from "@tanstack/react-query";
import { produce } from "immer";
import { toast } from "react-toastify";

import useWorkspaceCacheUpdate from "common/datahooks/useWorkspaceCacheUpdate";
import { del, post } from "common/helpers/HTTP";
import type {
  AppSumoPromotion,
  DetailedWorkspace,
  Promotion,
} from "common/types";

async function addLifetimeLicensesRequest(
  workspaceId: string,
  count: number,
  source: "appsumo" | "ltd",
): Promise<DetailedWorkspace> {
  return (
    await post(`workspaces/${workspaceId}/licenses/lifetime`, {
      count,
      source,
    })
  ).workspace;
}
async function createAppSumoPromotionRequest(code: string): Promise<Promotion> {
  return (await post(`customers/promotions/appsumo`, { code })).promotion;
}
async function resetSeatsRequest(
  source: "appsumo" | "ltd",
): Promise<Promotion> {
  return (await del(`customers/promotions/${source}`)).promotion;
}
export default function usePromotionsMutations() {
  const { updateCachedWorkspace } = useWorkspaceCacheUpdate();
  const queryClient = useQueryClient();

  const {
    mutateAsync: createAppSumoPromotion,
    isPending: isCreatingAppSumoPromotion,
    error: createAppSumoPromotionError,
  } = useMutation({
    mutationFn: ({ code }: { code: string }) =>
      createAppSumoPromotionRequest(code),
    onSuccess: (promotion) => {
      queryClient.setQueryData<Promotion[]>(["promotions"], (prevPromotions) =>
        prevPromotions ? [...prevPromotions, promotion] : [promotion],
      );
    },
  });

  const { mutateAsync: resetSeats, isPending: isResettingSeats } = useMutation({
    mutationFn: ({ source }: { source: "appsumo" | "ltd" }) =>
      resetSeatsRequest(source),
    onSuccess: (updatedPromotion, { source }) => {
      queryClient.invalidateQueries({ queryKey: ["workspaces"] });

      queryClient.setQueryData<Promotion[]>(
        ["promotions"],
        (prevPromotions) => {
          if (prevPromotions) {
            return produce(prevPromotions, (draftPromotions) => {
              for (let i = 0; i < draftPromotions.length; i += 1) {
                if (draftPromotions[i].type === source) {
                  draftPromotions[i] = updatedPromotion;
                }
              }
            });
          }
        },
      );

      toast.success("Seats reset successfully");
    },
    onError: () => toast.error("Failed to reset seats"),
  });

  const {
    mutateAsync: addLifetimeLicenses,
    isPending: isAddingLifetimeLicenses,
    error: addLifetimeLicensesError,
  } = useMutation({
    mutationFn: ({
      workspaceId,
      count,
      source,
    }: {
      workspaceId: string;
      count: number;
      source: "ltd" | "appsumo";
    }) => addLifetimeLicensesRequest(workspaceId, count, source),
    onSuccess: (updatedWorkspace, { workspaceId, count, source }) => {
      toast.success(`${count} seat${count > 1 ? "s" : ""} added`);

      updateCachedWorkspace(workspaceId, (draftWorkspace) => {
        const { licenses } = draftWorkspace;

        for (let i = 0; i < count; i += 1) {
          if (licenses) {
            licenses.push({
              status: "active",
              expires_at: 0,
              source,
              lifetime: true,
            });
          }
        }

        draftWorkspace.license_count += count;
        draftWorkspace.lifetime_license_count += count;
      });

      queryClient.setQueryData<Promotion[]>(
        ["promotions"],
        (prevPromotions) => {
          if (prevPromotions) {
            return produce(prevPromotions, (draftPromotions) => {
              for (let i = 0; i < draftPromotions.length; i += 1) {
                if (draftPromotions[i].type === source) {
                  draftPromotions[i].unassigned_license_count -= count;
                }
                if (draftPromotions[i].type === "appsumo") {
                  // the total promotion licenses in the updated workspace
                  const promotionLicensesCount =
                    updatedWorkspace.licenses.filter(
                      (license) => license.source === source,
                    ).length;
                  if (promotionLicensesCount === count) {
                    (
                      draftPromotions[i] as AppSumoPromotion
                    ).unassigned_workspaces -= 1;
                  }
                }
              }
            });
          }
        },
      );
    },
    onError: () => toast.error("Error adding seats"),
  });
  return {
    createAppSumoPromotion,
    isCreatingAppSumoPromotion,
    createAppSumoPromotionError,
    resetSeats,
    isResettingSeats,
    addLifetimeLicenses,
    isAddingLifetimeLicenses,
    addLifetimeLicensesError,
  };
}
